import "@hotwired/turbo-rails";
import { application } from "./controllers/application";
import ConnectivityController from "./controllers/connectivity_controller";
import DeferredVideoController from "./controllers/deferred_video_controller";
import LandingNavController from "./controllers/landing_nav_controller";
import LazyVideoController from "./controllers/lazy_video_controller";
import LocaleSwitcherController from "./controllers/locale_switcher_controller";
import ThemeController from "./controllers/theme_controller";
import "./theme";
import "./toast";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from "lenis";

application.register("connectivity", ConnectivityController);
application.register("deferred-video", DeferredVideoController);
application.register("landing-nav", LandingNavController);
application.register("lazy-video", LazyVideoController);
application.register("locale-switcher", LocaleSwitcherController);
application.register("theme", ThemeController);

gsap.registerPlugin(ScrollTrigger);

let lenis = null;
let landingContext = null;
let isReducedMotion = false;
let isMobile = false;

// ── 1. LENIS ARCHITECTURE ──────────────────────────────────────────────────
function onRaf(time) {
  if (lenis) {
    lenis.raf(time * 1000);
  }
}

function initLenis() {
  if (lenis || isReducedMotion || isMobile) return;
  
  lenis = new Lenis({
    smoothWheel: true,
    syncTouch: false,
    lerp: 0.08
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(onRaf);
  gsap.ticker.lagSmoothing(0);
}

// ── HELPER: CHARACTER SPLITTING ──────────────────────────────────────────────
function splitTextToChars(element, addCursor = false) {
  if (!element || isReducedMotion) return { chars: [], cursor: null };
  const nodes = Array.from(element.childNodes);
  let charElements = [];
  
  element.innerHTML = "";
  
  nodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const chars = node.textContent.split("");
      chars.forEach(char => {
        if (char === " " || char === "\n") {
          element.appendChild(document.createTextNode(char));
        } else {
          const span = document.createElement("span");
          span.className = "typing-char";
          span.style.opacity = "0";
          span.textContent = char;
          element.appendChild(span);
          charElements.push(span);
        }
      });
    } else if (node.nodeName === "BR") {
      element.appendChild(document.createElement("br"));
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      element.appendChild(node.cloneNode(true));
    }
  });

  let cursor = null;
  if (addCursor) {
    cursor = document.createElement("span");
    cursor.className = "typing-cursor";
    cursor.style.opacity = "0";
    cursor.style.display = "inline-block";
    cursor.style.width = "2px";
    cursor.style.height = "0.9em";
    cursor.style.backgroundColor = "currentColor";
    cursor.style.marginLeft = "4px";
    cursor.style.verticalAlign = "baseline";
    cursor.style.position = "relative";
    cursor.style.top = "0.1em";
    cursor.setAttribute("aria-hidden", "true");
    element.appendChild(cursor);
  }
  
  return { chars: charElements, cursor: cursor };
}

function createTypingTimeline(chars, cursor) {
  const tl = gsap.timeline();
  let currentTime = 0;
  
  chars.forEach((charSpan, index) => {
    // Natural typing irregularity: 55ms to 90ms
    let delay = gsap.utils.random(0.055, 0.09);
    
    if (index > 0) {
      const prevChar = chars[index - 1].textContent;
      if (prevChar === '.' || prevChar === '?' || prevChar === '!') {
        delay += 0.3; // Noticeably longer pause for punctuation
      } else if (prevChar === ',') {
        delay += 0.15;
      }
      
      // Check if there is a space before this character
      const prevNode = chars[index - 1];
      if (prevNode.nextSibling && prevNode.nextSibling.nodeType === Node.TEXT_NODE) {
        delay += 0.08; // Slightly longer pause for space
      }
    }
    
    currentTime += delay;
    
    // Instant appearance, no fade
    tl.set(charSpan, { opacity: 1 }, currentTime);
  });

  if (cursor) {
    // Show cursor slightly after last char
    tl.set(cursor, { opacity: 1 }, currentTime + 0.1);
    // Blink continuously
    tl.to(cursor, { opacity: 0, duration: 0.01, repeat: -1, yoyo: true, repeatDelay: 0.5, ease: "none" }, currentTime + 0.6);
  }
  
  return tl;
}

// ── 2. HERO ENTRANCE ───────────────────────────────────────────────────────
function initHero() {
  // Initialize video offsets
  const heroVideos = document.querySelectorAll('.gsap-hero-video');
  heroVideos.forEach(video => {
    const startTime = parseFloat(video.getAttribute('data-start-time')) || 0;
    if (startTime > 0) {
      if (video.readyState >= 1) {
        video.currentTime = startTime;
      } else {
        video.addEventListener('loadedmetadata', () => {
          video.currentTime = startTime;
        }, { once: true });
      }
    }
  });

  if (isReducedMotion) return;

  const heroTitle = document.querySelector(".landing-hero__title");
  let heroTextObj = null;
  if (heroTitle) {
    heroTextObj = splitTextToChars(heroTitle, true);
  }

  const tl = gsap.timeline({ delay: 0.1 });
  
  tl.from(".gsap-hero-el", {
    y: 60,
    opacity: 0,
    duration: 1.2,
    stagger: 0.1,
    ease: "power3.out",
    clearProps: "all"
  }, 0);

  if (heroTextObj && heroTextObj.chars.length > 0) {
    const typingTl = createTypingTimeline(heroTextObj.chars, heroTextObj.cursor);
    tl.add(typingTl, 0.2); // Start slightly after parent block reveals
  }

  tl.from(".gsap-hero-visual", {
    y: 100,
    opacity: 0,
    duration: 1.5,
    ease: "power4.out",
    clearProps: "all"
  }, 0.2);
  
  // Continuous Subtle iPhone Float
  if (!isMobile) {
    gsap.to(".gsap-iphone", {
      y: -15,
      rotationX: 2,
      rotationY: -2,
      duration: 4,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });
    
    // Subtle Collage Motion
    gsap.to(".gsap-collage-1", {
      y: -20,
      duration: 6,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });
    
    gsap.to(".gsap-collage-2", {
      rotation: 8,
      duration: 8,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });
    
    gsap.to(".gsap-collage-3", {
      scale: 1.05,
      duration: 5,
      ease: "sine.inOut",
      yoyo: true,
      repeat: -1
    });
  }
}

// ── 3. INTERNAL MEDIA PARALLAX ─────────────────────────────────────────────
function initMediaParallax() {
  if (isReducedMotion) return;

  const isMobileSize = window.innerWidth <= 767;
  // Reduce parallax movement on mobile to prevent excessive drifting and overlap
  const parallaxMultiplier = isMobileSize ? 0.4 : 1.0;

  const parallaxItems = gsap.utils.toArray(".gsap-parallax-internal");
  const galleryItems = gsap.utils.toArray(".gsap-gallery-item");
  
  if (galleryItems.length > 0) {
    // Modern coordinated gallery parallax
    galleryItems.forEach(item => {
      const mediaWrap = item.querySelector(".gallery-item__media-wrap");
      const inner = item.querySelector(".gsap-parallax-internal");
      const meta = item.querySelector(".gsap-meta-parallax");
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
      
      // 1. Outer Media Parallax
      // We animate the media wrapper instead of the article itself to avoid 
      // conflicting with the initScrollReveals() y-axis reveal animation.
      const itemSpeed = parseFloat(item.getAttribute("data-item-speed")) || 0;
      if (itemSpeed !== 0 && mediaWrap) {
        const distance = itemSpeed * 1000 * parallaxMultiplier;
        tl.fromTo(mediaWrap, { y: -distance }, { y: distance, ease: "none" }, 0);
      }
      
      // 2. Inner Image Parallax (Preserving existing logic/values)
      if (inner) {
        const imgSpeed = parseFloat(inner.getAttribute("data-speed")) || 0.1;
        const imgDistance = imgSpeed * 100 * parallaxMultiplier;
        tl.fromTo(inner, { yPercent: -imgDistance }, { yPercent: imgDistance, ease: "none" }, 0);
      }
      
      // 3. Metadata Depth
      // Moves slightly differently from the media wrap to create a subtle separation.
      if (meta) {
        const metaSpeed = parseFloat(meta.getAttribute("data-meta-speed")) || 0;
        if (metaSpeed !== 0) {
          // If the meta speed implies relative movement to the item speed, we 
          // add the itemSpeed to keep it anchored to the overall item's drift, 
          // then add the unique meta offset.
          const combinedSpeed = itemSpeed + metaSpeed;
          const distance = combinedSpeed * 1000 * parallaxMultiplier;
          tl.fromTo(meta, { y: -distance }, { y: distance, ease: "none" }, 0);
        }
      }
    });
  } else {
    // Fallback for standalone parallax images
    parallaxItems.forEach(inner => {
      const wrapper = inner.parentElement;
      const speed = parseFloat(inner.getAttribute("data-speed")) || 0.1;
      
      gsap.fromTo(inner, 
        { yPercent: -speed * 100 },
        {
          yPercent: speed * 100,
          ease: "none",
          scrollTrigger: {
            trigger: wrapper,
            start: "top bottom",
            end: "bottom top",
            scrub: true
          }
        }
      );
    });
  }
}

// ── 4. PRODUCT DEMO UI STATES ──────────────────────────────────────────────
function initProductDemo() {
  const demoSection = document.querySelector(".landing-demo");
  if (!demoSection) return;

  const states = gsap.utils.toArray(".demo-step");
  const video = document.querySelector(".demo-result-video");
  const genOverlay = document.querySelector(".demo-visual__generating-overlay");
  
  if (!states.length) return;
  const stepCount = states.length;

  const imgBase = document.querySelector(".demo-image-base");
  const uiUpload = document.querySelector(".demo-ui-layer--upload");
  const uiPersonalize = document.querySelector(".demo-ui-layer--personalize");
  const uiGenerating = document.querySelector(".demo-ui-layer--generating");
  
  const promptTextEl = document.querySelector(".gsap-demo-prompt");
  const progressFillEl = document.querySelector(".gsap-demo-progress-fill");
  const progressPercentEl = document.querySelector(".gsap-demo-percent");
  const progressStatusEl = document.querySelector(".gsap-demo-status");

  const fullPrompt = "Turn this into a cinematic travel scene";

  // Initial state setup
  gsap.set(states, { opacity: 0, y: 12 });
  gsap.set(states[0], { opacity: 1, y: 0 });
  states[0].classList.add("is-active");

  function updateDemoVisuals(progress) {
    const scaled = progress * (stepCount - 1); // 0 to 3

    // 1. Text Steps (Continuous overlap morphing)
    states.forEach((step, i) => {
      const dist = scaled - i; 
      let opacity = 0;
      let y = 0;
      
      // Overlapping transition zone of +/- 0.8
      if (Math.abs(dist) < 0.8) {
        opacity = gsap.utils.clamp(0, 1, 1 - (Math.abs(dist) / 0.6)); // fully visible at 0, fades out smoothly
        y = -dist * 12; // smoothly moves opposite to scroll
      } else {
        opacity = 0;
        y = dist > 0 ? -12 : 12;
      }
      
      gsap.set(step, { autoAlpha: opacity, y: y, force3D: true });
      if (Math.abs(dist) < 0.5) step.classList.add("is-active");
      else step.classList.remove("is-active");
    });

    // Unified slow continuous scale on the base image
    if (imgBase) {
      const p = gsap.utils.clamp(0, 1, scaled / 3); 
      gsap.set(imgBase, { scale: 1.0 + (p * 0.05), force3D: true });
    }

    // 2. Upload UI (Step 01, scaled ≈ 0)
    let uploadOpacity = 0;
    if (scaled <= 0.8) {
      uploadOpacity = gsap.utils.clamp(0, 1, 1 - (scaled / 0.8)); // 0 -> 0.8 (Fade out)
    }
    if (uiUpload) gsap.set(uiUpload, { autoAlpha: uploadOpacity, y: (1 - uploadOpacity) * 10 });

    // 3. Personalize UI (Step 02, scaled ≈ 1)
    let persOpacity = 0;
    if (scaled > 0.4 && scaled <= 1.6) {
      persOpacity = gsap.utils.clamp(0, 1, (scaled - 0.4) / 0.4); // 0.4 -> 0.8 (Fade in)
    } else if (scaled > 1.6) {
      persOpacity = gsap.utils.clamp(0, 1, 1 - (scaled - 1.6) / 0.4); // 1.6 -> 2.0 (Fade out)
    }
    if (uiPersonalize) gsap.set(uiPersonalize, { autoAlpha: persOpacity, y: (1 - persOpacity) * 10 });

    if (promptTextEl) {
      // Map scroll progress to string length
      const typeProgress = gsap.utils.clamp(0, 1, (scaled - 0.7) / 0.7); // Types out from 0.7 to 1.4
      
      // Add slight irregular typing feel mathematically
      // Map linear progress through a slight sine wave distortion so it's not perfectly robotic
      const distortedProgress = typeProgress + (Math.sin(typeProgress * Math.PI * 8) * 0.015);
      const clampedDistorted = gsap.utils.clamp(0, 1, distortedProgress);
      
      const charCount = Math.floor(clampedDistorted * fullPrompt.length);
      promptTextEl.textContent = fullPrompt.substring(0, charCount);
    }

    // 4. Generating UI (Step 03, scaled ≈ 2)
    let genOpacity = 0;
    if (scaled > 1.4 && scaled <= 2.4) {
       genOpacity = gsap.utils.clamp(0, 1, (scaled - 1.4) / 0.4); // 1.4 -> 1.8 (Fade in)
    } else if (scaled > 2.4) {
       genOpacity = gsap.utils.clamp(0, 1, 1 - (scaled - 2.4) / 0.4); // 2.4 -> 2.8 (Fade out)
    }
    if (uiGenerating) gsap.set(uiGenerating, { autoAlpha: genOpacity, y: (1 - genOpacity) * 10 });

    if (progressFillEl && progressPercentEl && progressStatusEl) {
      const genProgress = gsap.utils.clamp(0, 1, (scaled - 1.6) / 0.8); // 1.6 -> 2.4 builds to 100%
      progressFillEl.style.width = (genProgress * 100) + "%";
      progressPercentEl.textContent = Math.floor(genProgress * 100) + "%";
      
      if (genProgress < 0.3) progressStatusEl.textContent = "Analyzing image";
      else if (genProgress < 0.6) progressStatusEl.textContent = "Creating motion";
      else if (genProgress < 0.9) progressStatusEl.textContent = "Rendering scene";
      else progressStatusEl.textContent = "Finalizing video";
    }

    // 5. Result Video (Step 04, scaled ≈ 3)
    if (video) {
       // Start video playing silently early (around 1.8, mid-generation)
       if (scaled > 1.8) {
         if (video.paused) {
            video.play().catch(() => {});
         }
       } else {
         if (!video.paused) {
            video.pause();
            if (video.readyState >= 1) video.currentTime = 0;
         }
       }
       
       // Early Crossfade! video enters BEFORE Result step (2.3 -> 2.7)
       let vidOpacity = 0;
       if (scaled > 2.3) {
         vidOpacity = gsap.utils.clamp(0, 1, (scaled - 2.3) / 0.4);
       }
       
       gsap.set(video, { autoAlpha: vidOpacity });
       
       // Fade out the base upload image underneath
       if (imgBase) {
         gsap.set(imgBase, { autoAlpha: 1 - vidOpacity });
       }
    }
  }

  const mm = gsap.matchMedia();

  // Desktop pinned scroll interaction
  mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
    const proxy = { p: 0 };
    
    gsap.to(proxy, {
      p: 1,
      ease: "none",
      scrollTrigger: {
        trigger: demoSection,
        pin: true,
        start: "center center",
        end: "+=" + (stepCount * 100) + "%", 
        scrub: 0.5, 
        refreshPriority: 10,
        onUpdate: () => updateDemoVisuals(proxy.p)
      }
    });

    // Run once to initialize
    updateDemoVisuals(0);
  });

  // Mobile / Reduced Motion fallback
  // Just fade them as they scroll naturally, no pinned proxy loop
  mm.add("(max-width: 1023px), (prefers-reduced-motion: reduce)", () => {
    // Basic natural progression based on scrolling the section itself
    ScrollTrigger.create({
      trigger: demoSection,
      start: "top center",
      end: "bottom center",
      scrub: true,
      onUpdate: (self) => updateDemoVisuals(self.progress)
    });
  });
}

// ── 5. IMAGE-TO-VIDEO TRANSFORMATION ───────────────────────────────────────
function initImageToVideo() {
  if (isReducedMotion) return;

  const section = document.querySelector(".landing-transform");
  if (!section) return;

  const header = section.querySelector(".transform-header");
  const visual = section.querySelector(".transform-visual");
  const source = section.querySelector(".transform-visual__source");
  const result = section.querySelector(".transform-visual__result");
  const resultVideo = section.querySelector(".transform-result-video");
  const scanner = section.querySelector(".transform-scanner");
  const labels = section.querySelectorAll(".transform-label");
  const stickyTitle = section.querySelector(".sticky-title");

  let stickyTextObj = null;
  let stickyTypingTl = null;
  
  if (stickyTitle) {
    stickyTextObj = splitTextToChars(stickyTitle, true);
    if (stickyTextObj && stickyTextObj.chars.length > 0) {
      stickyTypingTl = createTypingTimeline(stickyTextObj.chars, stickyTextObj.cursor);
      stickyTypingTl.pause();
    }
  }

  // Initial cinematic states
  gsap.set(visual, { y: "60vh", scale: 0.95 });
  gsap.set(header, { opacity: 1, scale: 1 });
  gsap.set(labels, { opacity: 0, y: 10 });
  
  // Single coordinated timeline mapped to scroll progress
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "center center", 
      end: "+=220%", // 2.2 viewport heights of scroll duration
      pin: true,
      scrub: 1, // smooth scrubbing
      refreshPriority: 5, // FIX: Calculate after #demo (which is priority 10)
      onEnter: () => {
        if (stickyTypingTl && stickyTypingTl.progress() === 0) {
          stickyTypingTl.play();
        }
        if (resultVideo && resultVideo.paused) {
          resultVideo.play().catch(() => {});
        }
      },
      onEnterBack: () => {
        if (stickyTypingTl && stickyTypingTl.progress() === 0) {
          stickyTypingTl.play();
        }
        if (resultVideo && resultVideo.paused) {
          resultVideo.play().catch(() => {});
        }
      },
      onLeave: () => {
        if (resultVideo && !resultVideo.paused) {
          resultVideo.pause();
        }
      },
      onLeaveBack: () => {
        if (resultVideo && !resultVideo.paused) {
          resultVideo.pause();
        }
      }
    }
  });

  // PHASE 1 & 2: Visual moves up and overlaps header. Header recedes BEHIND visual before fading.
  tl.to(visual, { y: "0vh", scale: 1, duration: 1.5, ease: "power2.inOut" }, 0)
    .to(header, { y: "-15vh", scale: 0.95, duration: 1.0, ease: "power2.in" }, 0)
    .to(header, { opacity: 0, duration: 0.5, ease: "power1.in" }, 1.0);
    
  // Initial label entrance
  const labelSource = labels[0];
  const labelResult = labels[1];
  tl.to(labelSource, { opacity: 1, y: 0, duration: 0.5 }, 0.8);
  tl.to(labelResult, { opacity: 0.4, y: 0, duration: 0.5 }, 1.0);
    
  // PHASE 3 & 4: Scanner reveals AI result
  tl.fromTo(result, 
    { clipPath: "inset(0 100% 0 0)" },
    { clipPath: "inset(0 0% 0 0)", duration: 2, ease: "power1.inOut" },
    1.2
  );
  
  tl.fromTo(scanner,
    { left: "0%" },
    { left: "100%", duration: 2, ease: "power1.inOut" },
    1.2
  );
  
  

  // Video emergence (opacity fade creates a soft overlap with the clip-path)
  if (resultVideo) {
    tl.fromTo(resultVideo,
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 1.4, ease: "power2.inOut" },
      1.6 // Video begins emerging at 1.6, solidifies near 3.0
    );
  }
  
  // Labels evolve to highlight AI MOTION
  tl.to(labelSource, { opacity: 0.4, duration: 1.0, ease: "power1.inOut" }, 2.0);
  if (labelResult) {
    tl.to(labelResult, { opacity: 1, duration: 1.0, ease: "power1.inOut" }, 2.0);
  }
  
  // Continuous parallax scale
  tl.to(source, { scale: 1.04, duration: 3.2, ease: "none" }, 0);
  tl.fromTo(result, { scale: 1.04 }, { scale: 1, duration: 3.2, ease: "none" }, 0);
  
  // Final settle
  tl.to(visual, { scale: 1.02, duration: 0.8, ease: "none" });
}

// ── 6. SCROLL REVEALS ──────────────────────────────────────────────────────
function initScrollReveals() {
  if (isReducedMotion) return;

  // Showcase
  const showcaseItems = gsap.utils.toArray(".gsap-gallery-item");
  showcaseItems.forEach(item => {
    gsap.from(item, {
      scrollTrigger: { trigger: item, start: "top 85%" },
      y: 80, opacity: 0, duration: 1.2, ease: "power3.out", clearProps: "all"
    });
  });

  // Features
  const features = gsap.utils.toArray(".gsap-feature-row");
  features.forEach(feature => {
    gsap.from(feature, {
      scrollTrigger: { trigger: feature, start: "top 85%" },
      y: 60, opacity: 0, duration: 1, ease: "power3.out", clearProps: "all"
    });
  });

  // CTA
  const cta = document.querySelector(".gsap-cta-content");
  if (cta) {
    gsap.from(cta, {
      scrollTrigger: { trigger: ".landing-final-cta", start: "top 85%" },
      y: 80, opacity: 0, duration: 1.5, ease: "power4.out", clearProps: "all"
    });
  }
}

// ── 7. HOVER INTERACTIONS ──────────────────────────────────────────────────
function initHoverInteractions() {
  if (isReducedMotion || isMobile) return;

  const magnets = document.querySelectorAll('.btn--magnetic');
  magnets.forEach(btn => {
    const xTo = gsap.quickTo(btn, "x", { duration: 0.4, ease: "power3.out" });
    const yTo = gsap.quickTo(btn, "y", { duration: 0.4, ease: "power3.out" });

    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      xTo(x * 0.3);
      yTo(y * 0.3);
    });
    
    btn.addEventListener('mouseleave', () => {
      xTo(0);
      yTo(0);
    });
  });
}

// ── 8. PROCESS INTERACTIVE REVEALS ───────────────────────────────────────────
function initProcess() {
  const rows = gsap.utils.toArray(".gsap-process-row");
  const markers = gsap.utils.toArray(".gsap-process-marker");
  const progressFill = document.querySelector(".gsap-process-progress");
  const processLayout = document.querySelector(".process-layout");
  
  // Header animation
  const header = document.querySelector(".process-header");
  const eyebrow = document.querySelector(".process-header .landing-eyebrow");
  const title = document.querySelector(".process-main-title");
  
  if (header && !isReducedMotion) {
    gsap.set([eyebrow, title], { opacity: 0, y: 30 });
    ScrollTrigger.create({
      trigger: header,
      start: "top 80%",
      onEnter: () => {
        gsap.to(eyebrow, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" });
        gsap.to(title, { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: "power2.out" });
      }
    });
  }

  if (rows.length === 0) return;

  if (isReducedMotion) {
    rows.forEach(row => {
      const content = row.querySelector(".gsap-process-content");
      const visual = row.querySelector(".gsap-process-visual");
      if (content && visual) gsap.set([content, visual], { opacity: 1, y: 0, scale: 1 });
    });
    return;
  }

  // Row entrances
  rows.forEach((row) => {
    const content = row.querySelector(".gsap-process-content");
    const visual = row.querySelector(".gsap-process-visual");
    
    // Set initial state for reveal
    if (content) gsap.set(content, { opacity: 0, y: 30 });
    if (visual) gsap.set(visual, { opacity: 0, scale: 0.96 });

    // Trigger reveal when entering viewport
    ScrollTrigger.create({
      trigger: row,
      start: "top 80%",
      onEnter: () => {
        if (content) gsap.to(content, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" });
        if (visual) gsap.to(visual, { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out" });
      }
    });
  });

  // Desktop active states & progress rail
  if (window.innerWidth >= 1024 && processLayout && progressFill && markers.length > 0) {
    
    // 1. Physically Synchronize Markers with Row Geometry
    function positionMarkers() {
      const layoutRect = processLayout.getBoundingClientRect();
      
      rows.forEach((row, i) => {
        if (!markers[i]) return;
        
        // Align marker to the visual center of the editorial content block
        const rowContent = row.querySelector('.gsap-process-content');
        let alignOffset = 0;
        
        if (rowContent) {
           const contentRect = rowContent.getBoundingClientRect();
           alignOffset = (contentRect.top - layoutRect.top) + (contentRect.height / 2);
        } else {
           const rowRect = row.getBoundingClientRect();
           alignOffset = (rowRect.top - layoutRect.top) + (rowRect.height / 2);
        }
        
        // Offset by half marker height (~12px) for perfect center alignment
        markers[i].style.top = `${alignOffset - 12}px`;
      });
      
      ScrollTrigger.refresh();
    }
    
    positionMarkers();
    
    // Recalculate on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(positionMarkers, 150);
    });

    // 2. Continuous Scroll Progress (Viewport Tracking)
    ScrollTrigger.create({
      trigger: processLayout,
      start: "top center",
      end: "bottom center",
      scrub: true,
      onUpdate: (self) => {
        // The fill perfectly tracks the viewport center passing through the section
        const layoutHeight = processLayout.getBoundingClientRect().height;
        let currentFillHeight = self.progress * layoutHeight;
        
        // Cap the fill at the final marker so it stops drawing when complete
        const lastMarker = markers[markers.length - 1];
        if (lastMarker) {
          const maxFillHeight = parseInt(lastMarker.style.top || "0") + 12; // Center of marker
          if (currentFillHeight > maxFillHeight) currentFillHeight = maxFillHeight;
        }
        
        progressFill.style.height = `${currentFillHeight}px`;
      }
    });

    // 3. Smooth Marker Activation (Active & Complete States)
    rows.forEach((row, i) => {
      ScrollTrigger.create({
        trigger: row,
        start: "top 55%", // Triggers slightly below center
        end: "bottom 55%", 
        onToggle: (self) => {
          if (self.isActive) {
            // Update row visibility
            rows.forEach(r => r.classList.remove("is-active"));
            row.classList.add("is-active");
            
            // Update marker sequence
            markers.forEach((m, idx) => {
              m.classList.remove("is-active", "is-complete");
              if (idx < i) {
                m.classList.add("is-complete"); // Past
              } else if (idx === i) {
                m.classList.add("is-active"); // Current
              }
            });
          }
        }
      });
    });

    // Smooth scroll for clicks
    markers.forEach(marker => {
      marker.addEventListener("click", (e) => {
        e.preventDefault();
        const targetSelector = marker.getAttribute("data-target");
        const targetEl = document.querySelector(targetSelector);
        if (targetEl) {
          if (typeof lenis !== 'undefined' && lenis) {
            lenis.scrollTo(targetEl, { offset: -120, duration: 1.2 });
          } else {
            const y = targetEl.getBoundingClientRect().top + window.scrollY - 120;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }
      });
    });
  }
}

// ── 8. BRAND OUTRO ───────────────────────────────────────────────────────────
function initBrandOutro() {
  const outro = document.querySelector('.landing-brand-outro');
  const wordmark = document.querySelector('.landing-brand-outro__wordmark');
  
  if (outro && wordmark && !isReducedMotion) {
    // Elegant, slow scroll reveal (no scrub, no bounce)
    ScrollTrigger.create({
      trigger: outro,
      start: "top 75%",
      onEnter: () => {
        gsap.to(wordmark, {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 1.8,
          ease: "power2.out",
          overwrite: "auto"
        });
      }
    });

    // Subtle physical light reflection on hover (desktop only)
    if (!isMobile) {
      outro.addEventListener('mousemove', (e) => {
        const rect = outro.getBoundingClientRect();
        // Calculate percentage position
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        outro.style.setProperty('--x', `${x}%`);
        outro.style.setProperty('--y', `${y}%`);
      });
    }
  } else if (wordmark) {
    // Fallback for reduced motion
    gsap.set(wordmark, { opacity: 1, scale: 1, y: 0 });
  }
}

// ── INIT & CLEANUP ─────────────────────────────────────────────────────────
function init() {
  if (landingContext) cleanup();
  
  isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  isMobile = window.innerWidth <= 767;

  initLenis();

  landingContext = gsap.context(() => {
    initHero();
    initMediaParallax();
    initProductDemo();
    initImageToVideo();
    initProcess();
    initScrollReveals();
    initHoverInteractions();
    initBrandOutro();
  }, document.body);

  if (document.fonts) {
    document.fonts.ready.then(() => {
      ScrollTrigger.refresh();
    });
  } else {
    setTimeout(() => ScrollTrigger.refresh(), 500);
  }
}

function cleanup() {
  if (landingContext) {
    landingContext.revert(); 
    landingContext = null;
  }
  if (lenis) {
    lenis.destroy();
    gsap.ticker.remove(onRaf);
    lenis = null;
  }
}

document.addEventListener("turbo:load", init);
document.addEventListener("turbo:before-cache", cleanup);
